// @ts-check

/** @import { FC } from "react"; */
/** @import { ScopeDraft } from "./ScopesTable/types"; */

import { useMemo, useState } from "react";
import NoDataFound from "../../../../components/NoDataFound/NoDataFound.jsx";
import TableLoader from "../../../../components/Loader/TableLoader.jsx";
import Validations from "../../../../Regex.js";
import toast from "react-hot-toast";
import ScopeTable from "./ScopesTable";
import Mandotory from "../../../../components/Input/Mandotory.jsx";
import { isHistoricOptions } from "../Data/data";
import MaxCharacterCount from "../../../../components/Input/MaxCharacterCount.jsx";

import { addScope } from "../Api/Scopes/scopes";
import usePermissions from "../../../../components/CustomHooks/usePermissions.js";
import { useMutation, useQuery } from "@tanstack/react-query";
import { accounts } from "src/queries/accounts";
import { EMPTY_ARRAY } from "src/utils/empty-values";

const scopeOptions = [
  { label: "Price Claims", value: "price_claims" },
  { label: "Shortages", value: "shortages" },
  { label: "Chargebacks", value: "chargebacks" },
  { label: "Accruals", value: "accruals" },
  { label: "Vendor Returns", value: "vendor_returns" },
];

/** @type {ScopeDraft} */
const initialScopeDraft = {
  name: "",
  activation_date: null,
  deactivation_date: null,
  is_historic: null,
  invoice_start_date: null,
  invoice_end_date: null,
  notes: "",
};

/** @type {Record<keyof ScopeDraft, string>} */
const scopeDraftFieldLabelMap = {
  name: "Name",
  activation_date: "Activation Date",
  deactivation_date: "Deactivation Date",
  is_historic: "Historic",
  invoice_start_date: "Start Date Under Scope (invoice start date)",
  invoice_end_date: "End Date Under Scope (invoice due date)",
  notes: "Notes",
};

const scopeDraftFieldLabels = /** @type {[keyof ScopeDraft, string][]} */ (Object.entries(scopeDraftFieldLabelMap));

/**
 * @typedef {{
 *  accountId: string | number;
 *  onSuccessfulScopeUpdate: () => void;
 * }} AddScopesProps
 */

/** @type {FC<AddScopesProps>} */
const AddScopes = ({ accountId, onSuccessfulScopeUpdate }) => {
  const { hasPermission } = usePermissions();

  const accountScopesQuery = useQuery(accounts.detail(accountId)._ctx.scopes);
  const accountScopes = accountScopesQuery.data ?? EMPTY_ARRAY;
  const refetchScopes = () => accountScopesQuery.refetch();
  const areAccountScopesLoading = accountScopesQuery.isPending || accountScopesQuery.isFetching;

  const [scopeDraft, setScopeDraft] = useState(() => initialScopeDraft);

  const addScopeMutation = useMutation({
    mutationKey: ["account-scope-create", accountId],
    mutationFn: (/** @type {ScopeDraft} */ newScope) => addScope(accountId, newScope),
    onSuccess(response) {
      toast.success(response.message || "Scope added successfully");
      refetchScopes();
      onSuccessfulScopeUpdate();
      setScopeDraft(initialScopeDraft);
    },
  });

  const isAddingScope = addScopeMutation.isPending;

  const characterCount = scopeDraft.notes.length;
  const maxNotesCharacters = 255;

  /** @type {<K extends keyof ScopeDraft>(field: keyof ScopeDraft, value: ScopeDraft[K]) => void} */
  const handleScopeDraftChange = (field, value) => {
    const newFormData = { ...scopeDraft, [field]: value };
    if (field === "name" && value === "vendor_returns") {
      newFormData.is_historic = "false";
      newFormData.invoice_end_date = null;
    }
    if (field === "activation_date") {
      // Automatically update invoice_start_date based on the new activation_date
      const activationDate = new Date(/** @type {string} */ (value));
      const defaultStartDate = new Date(activationDate);
      const yearsBack = newFormData.name === "vendor_returns" ? 2 : 5;
      defaultStartDate.setFullYear(activationDate.getFullYear() - yearsBack);
      newFormData.invoice_start_date = defaultStartDate.toISOString().split("T")[0];
    }

    setScopeDraft(newFormData);
  };

  const onScopeDraftSubmit = () => {
    const massagedScopeDraft = {
      ...scopeDraft,
      is_historic: scopeDraft.is_historic === "true" ? true : scopeDraft.is_historic === "false" ? false : null,
    };

    const mandatoryFields = [
      { name: "Scopes Name", value: massagedScopeDraft.name },
      { name: "Activation Date", value: massagedScopeDraft.activation_date },
      { name: "Invoice Start Date", value: massagedScopeDraft.invoice_start_date },
      ...(massagedScopeDraft.name === "vendor_returns"
        ? []
        : [{ name: "Historic", value: massagedScopeDraft.is_historic }]),
    ];

    const emptyFields = Validations.checkMandatoryFields(mandatoryFields);

    if (emptyFields.length > 0) {
      toast.error(`${emptyFields[0]?.name} can't be empty`);
    } else if (massagedScopeDraft.is_historic === true && !massagedScopeDraft.invoice_end_date) {
      toast.error("End Date Under Scope (Invoice Due Date) is required when scope is marked as Historic");
    } else {
      if (massagedScopeDraft.deactivation_date === "") {
        massagedScopeDraft.deactivation_date = null;
      }
      addScopeMutation.mutate(massagedScopeDraft);
    }
  };

  const availableScopeOptions = useMemo(
    () => scopeOptions.filter(option => !accountScopes.some(scope => scope.NAME === option.value)),
    [accountScopes],
  );

  const invalidateAccountsAndScopes = () => {
    onSuccessfulScopeUpdate();
    refetchScopes();
  };

  return (
    <div className="container mt-4 ">
      {hasPermission(["admin:*", "user:client_config:write:*"]) && (
        <div className="bg_shadow bg-light-subtle p-5 mb-5">
          <label className="form-label fw-bold">Add Scopes</label>
          {availableScopeOptions.length > 0 ? (
            <>
              <div className="mb-4 rounded ">
                <div className="row">
                  {scopeDraftFieldLabels.map(([field, label]) => (
                    <div key={field} className="col-md-6 mb-3">
                      <label>
                        {label}{" "}
                        {(["activation_date", "name", "invoice_start_date"].includes(field) ||
                          (field === "is_historic" && scopeDraft.name !== "vendor_returns") ||
                          (field === "invoice_end_date" && scopeDraft.is_historic === "true")) && (
                          <Mandotory classes="text-red" />
                        )}
                      </label>
                      {field === "activation_date" ||
                      field === "deactivation_date" ||
                      field === "invoice_start_date" ||
                      field === "invoice_end_date" ? (
                        <input
                          type="date"
                          className="form-control "
                          value={scopeDraft[field] ?? ""}
                          onChange={e => handleScopeDraftChange(field, e.target.value)}
                          disabled={field === "invoice_end_date" && scopeDraft.name === "vendor_returns"}
                        />
                      ) : field === "notes" ? (
                        <div>
                          <textarea
                            className="form-control "
                            value={scopeDraft[field]}
                            onChange={e => handleScopeDraftChange(field, e.target.value)}
                            maxLength={maxNotesCharacters}
                          />
                          <MaxCharacterCount characterCount={characterCount} maxNotesCharacters={maxNotesCharacters} />
                        </div>
                      ) : field === "name" ? (
                        <select
                          className="form-control form-select"
                          value={scopeDraft[field]}
                          onChange={e => handleScopeDraftChange(field, e.target.value)}
                        >
                          <option value="">Select Scopes</option>
                          {availableScopeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : field === "is_historic" ? (
                        <select
                          className="form-control form-select"
                          value={String(scopeDraft[field] ?? "")}
                          onChange={e => handleScopeDraftChange(field, e.target.value)}
                          disabled={scopeDraft.name === "vendor_returns"}
                        >
                          <option value="">Select Historic Option</option>
                          {isHistoricOptions.map(option => (
                            <option key={String(option.value)} value={String(option.value)}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          value={scopeDraft[field]}
                          onChange={e => handleScopeDraftChange(field, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {!isAddingScope ? (
                <button type="submit" className="btn btn-primary" onClick={onScopeDraftSubmit}>
                  Add Scopes
                </button>
              ) : (
                <button className="btn btn-primary" type="button" disabled>
                  <span className="spinner-grow spinner-grow-sm" aria-hidden="true"></span>
                  <span role="status">Loading...</span>
                </button>
              )}
            </>
          ) : (
            <div className="alert alert-info" role="alert">
              All scope types have already been added to this store.
            </div>
          )}
        </div>
      )}

      {!areAccountScopesLoading ? (
        <>
          <div className="container-sm table_main_account border border-secondary">
            {accountScopes?.length >= 1 ? (
              <ScopeTable
                scopesData={accountScopes.map(scope => ({
                  ...scope,
                  ACTIVATION_DATE: scope.ACTIVATION_DATE
                    ? new Date(scope.ACTIVATION_DATE).toISOString().split("T")[0]
                    : null,
                  DEACTIVATION_DATE: scope.DEACTIVATION_DATE
                    ? new Date(scope.DEACTIVATION_DATE).toISOString().split("T")[0]
                    : null,
                  INVOICE_START_DATE: scope.INVOICE_START_DATE
                    ? new Date(scope.INVOICE_START_DATE).toISOString().split("T")[0]
                    : null,
                  INVOICE_END_DATE: scope.INVOICE_END_DATE
                    ? new Date(scope.INVOICE_END_DATE).toISOString().split("T")[0]
                    : null,
                }))}
                invalidateAccountsAndScopes={invalidateAccountsAndScopes}
              />
            ) : (
              <NoDataFound />
            )}
          </div>
        </>
      ) : (
        <TableLoader />
      )}
    </div>
  );
};

export default AddScopes;
